/*
 * Authors: mariushirjanu
 * Created: 01/06/2023
 */
import _ from 'underscore';
import { Component, EventEmitter, HostListener, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { TranslationService } from '../translation/translation.service';
import { CellValueChangedEvent, ColDef, EditableCallback, EditableCallbackParams, GridOptions, ICellRendererParams, IRowNode, ITooltipParams, ProcessCellForExportParams, RowClassParams, RowEditingStoppedEvent, RowGroupOpenedEvent, RowNode } from 'ag-grid-community';
import { DOCUMENT } from '@angular/common';
import { IObject } from '@type_definitions/datatypes';
import { FormlyInputType, FormlyType } from '../formly/ga-formly.service';
import { util } from '../util';
import { IColDef } from '../report/report-grid.service';
import { AgGridDomLayout, GaAgGridService } from '../ag-grid-components/ga-ag-grid.service';
import { GaEventService } from '../services/ga-event.service';

export const EVENT_ROW_DATA_CHANGED:string = 'formGridRowDataChanged';
export const CSS_ACTIONS_COLUMN:string = 'row-actions';

export interface IFormGridColDefParam {
  // use the same properties from IExtraTemplateOptions whenever possible
  type:FormlyType;
  inputType?:FormlyInputType;
  required?:boolean;
  options?:any[];
  optionsFn?:Function;
  format?:Function | string;
  addOn?:string;
  editableOnAdd?:boolean; // when true -> associated cell becomes editable when adding a new row (even if editable = false)
  editableOnUpdate?:boolean; // when true -> associated cell becomes editable when adding updating a row (even if editable = false)
  defaultValue?:any;  // value to be used as default in the cell
  isActionCell?:boolean; // true when the editing cell has buttons for actions
  isActionColumnDoubleWidth?:boolean; // to use only for when isActionCell=true
  onCancelEditing?:(rowIndex:number) => void;
  isAddMode?:() => boolean; // a boolean function that returns true when the cell is part of a new added row
  labelProp?:string;
  valueProp?:string;
  minLength?:number;
  maxLength?:number;
  minDate?:Date;
  maxDate?:Date;
  min?:number;
  pattern?:string;
  errorMessage?:string; // display a tooltip with an error message
  valueFormatter?:Function; // apply a formatter function to the input value
}

export interface IFormGridColDef extends ColDef {
  cellEditorParams?:IFormGridColDefParam;
}

const CSS_NON_SELECTED_ROWS:string = '.ag-row:not(.ag-row-selected):not(.ag-row-editing):not(.pointer-events-none)';

// in editing mode it uses ag-cell-editor.component.ts for rendering
@Component({
  selector: 'form-grid',
  template: `
    <div class="form-grid">
      <div *ngIf="showActionButtons" class="row">
        <div class="col-6 col-lg-space-bottom">
          <button [ngStyle]="{visibility: !allowAdd? 'hidden':''}" class="btn btn-primary" (click)="addRow()" [disabled]="loading || aRowIsEditing" translate [translateParams]="{ label: title }">
            GA.BUTTONS.ADD_LABEL
          </button>
        </div>
        <div class="col-6 row">
          <div class="float-end">
            <ng-content></ng-content>
          </div>
        </div>
      </div>
      
      <ga-ag-grid [gridId]="gridId" [gridOptions]="gridOptions" [gridInit]="gridInit" [height]="height"></ga-ag-grid>
    </div>
  `,
  styleUrls: [ 'form-grid.scss' ],
  encapsulation: ViewEncapsulation.None
})
export class FormGridComponent implements OnInit, OnChanges {
  @Input() public gridId:string;
  @Input() public title:string;
  @Input() public gridOptions:GridOptions;
  @Input() public rowData:IObject[]; // enables the update of rows at a later time
  @Input() public columnData:IObject[]; // enables the update of columns at a later time
  @Input() public height?:number;
  @Input() public allowAdd:boolean;
  @Input() public allowEdit:boolean;
  @Input() public allowDelete:boolean;
  @Input() public showActionButtons?:boolean;
  @Input() public noResultsText:string;
  @Input() public stopEditing:boolean;
  @Input() public loading:boolean;

  @Output() public onChange:EventEmitter<IObject[]> = new EventEmitter<IObject[]>();
  @Output() public onRowEditing:EventEmitter<boolean> = new EventEmitter<boolean>();

  public gridInit:boolean;
  public isAddMode:boolean;
  public aRowIsEditing:boolean;

  constructor(
    private translationService:TranslationService,
    @Inject(DOCUMENT) private document:Document,
    private eventService:GaEventService,
    private gaAgGridService:GaAgGridService
  ) {
    /***/
  }

  public ngOnInit():void {
    // install grid
    this.installGrid();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.gridId) {
      this.gridId = changes.gridId.currentValue;
    }

    if (changes.gridOptions) {
      this.gridOptions = changes.gridOptions?.currentValue || this.gridOptions || {};
      
      if (this.gridOptions.rowData?.length) {
        this.rowData = this.gridOptions.rowData;
      }
    }

    if (changes.loading) {
      this.loading = changes.loading.currentValue;
      this.gaAgGridService.setGridLoading(this.gridId, this.loading);
      this.statusActionButtons(this.loading);
    }

    if (changes.rowData) {
      this.rowData = changes.rowData.currentValue;
      this.gaAgGridService.setRowData(this.gridId, this.rowData);
    }

    if (changes.columnData) {
      this.columnData = changes.columnData.currentValue;
      this.gaAgGridService.setColumnDefs(this.gridId, this.getColDefs(this.columnData));
    }

    // trigger on every boolean value change
    if (changes.stopEditing) {
      this.gaAgGridService.stopEditing(this.gridId);
    }
  }

  private installGrid():void {
    const loadingText:string = this.title ? this.translationService.instant('GA.LOADING', { toload: this.translationService.instant(this.title) }) : this.translationService.instant('GA.LOADING_SIMPLE');
    const noResultsText:string = this.translationService.instant(this.noResultsText || 'GA.GENERAL.NO_RESULTS_FOUND');

    // overwrite initOptions
    this.gridOptions.columnDefs = this.getColDefs();

    // set default grid options
    this.gridOptions.editType = 'fullRow';
    this.gridOptions.onGridReady = this.onGridReady;
    this.gridOptions.onRowGroupOpened = this.onRowGroupOpened;
    this.gridOptions.scrollbarWidth = 15;
    this.gridOptions.headerHeight = 36;
    this.gridOptions.singleClickEdit = true;
    this.gridOptions.domLayout = this.height ? AgGridDomLayout.NORMAL : AgGridDomLayout.AUTO_HEIGHT;
    this.gridOptions.suppressColumnVirtualisation = true;
    this.gridOptions.tooltipShowDelay = 10;
    this.gridOptions.onRowEditingStarted = this.onRowEditingStarted;
    this.gridOptions.onRowEditingStopped = this.onRowEditingStopped;
    this.gridOptions.onCellValueChanged = this.gridOptions.onCellValueChanged || this.onCellValueChanged;
    this.gridOptions.rowClassRules = {
      ...this.gridOptions.rowClassRules,
      'pointer-events-none': (params:RowClassParams) => {
        if(!this.gridOptions.context?.preventExitEditing){
          return false;
        }
        return params.api?.getEditingCells()?.length > 0;
      }
    };
    this.gridOptions.processCellForClipboard = this.processCellForClipboard;
    this.gridOptions.overlayLoadingTemplate = `<div class="ag-overlay-loading-center"><i class="fa fa-spinner fa-spin"></i><div>${loadingText}…</div></div>`;
    this.gridOptions.overlayNoRowsTemplate = `<div class="ag-overlay-no-rows-center"><h2 translate>${noResultsText}</h2><div>`;

    this.gridOptions.defaultColDef = {};
    this.gridOptions.defaultColDef.filter = true;

    this.gridInit = true;
  }

  private onGridReady = ():void => {
    // A fallback for the first time when 'gridApi' is not ready in ngOnChanges
    if (this.rowData?.length) {
      this.gaAgGridService.setRowData(this.gridId, this.rowData);
    }
  };

  private getColDefs(colDefs?:IFormGridColDef[]):IFormGridColDef[] {
    colDefs = colDefs || this.gridOptions.columnDefs;

    const columnDefs:IFormGridColDef[] = [];

    _.each(colDefs, (colDef:IFormGridColDef) => {
      colDef.cellEditorParams = colDef?.cellEditorParams || <any>{};

      if(colDef.headerName){
        colDef.headerName = this.translationService.instant(colDef.headerName);
      }

      // overwrites editable parameter with a custom conditional logic
      const initialEditable:boolean|EditableCallback = colDef.editable;
      colDef.editable = (params:EditableCallbackParams) => this.isCellEditable(params, initialEditable) && this.allowEdit;

      // set isAddMode on the column definition to pass it to cell editor
      colDef.cellEditorParams.isAddMode = () => this.isAddMode;

      // Tooltip Options:
    // 1) default: 'headerName' is used as a tooltip for the cells.
    // 2) tooltipField: 'fieldName' -> the tooltip displays the cell value.
    // 3) tooltipField: '' -> no tooltip is shown.
      colDef.tooltipValueGetter = colDef.tooltipValueGetter || ((params:ITooltipParams):string => {
        if(util.isNullOrUndefined((<IFormGridColDef>params.colDef).tooltipField)){
          return params.colDef.headerName;
        }
        return !_.isEmpty((<IFormGridColDef>params.colDef).tooltipField) ? params.value : '';
      });

      columnDefs.push(colDef);
    });

    if(this.showActionButtons && (this.allowEdit || this.allowDelete)){
      columnDefs.push(<IFormGridColDef>this.getActionsColDef());
    }

    return columnDefs;
  }

  private getActionsColDef():IFormGridColDef {
    const width:number = this.isActionColumnDoubleWidth() ? 94 : 47;
    return {
      cellRenderer: (params:ICellRendererParams) => {
        //do not use angular here, it will seriously impact performance
        const divElm:HTMLDivElement = this.document.createElement('div');

        if(this.allowDelete) {
          divElm.innerHTML +=
            `<button class="btn btn-default btn-sm btn-delete" title="Delete">
              <i class="fa fa-trash-o"></i>
            </button>`;
        }

        if(this.allowEdit) {
          divElm.innerHTML +=
            `<button class="btn btn-default btn-sm btn-edit" title="Edit">
              <i class="fa fa-pencil"></i>
            </button>`;
        }

        const divAElm:JQuery = $(divElm);

        if(this.allowDelete) {
          divAElm.find('.btn-delete')
            .on('click', () => {
              this.deleteRow(params.data);
            });
        }

        if(this.allowEdit) {
          divAElm.find('.btn-edit')
            .on('click', () => {
              this.startRowEditing(params.node.rowIndex);
            });
        }

        return divElm;
      },
      cellClass: CSS_ACTIONS_COLUMN,
      headerName: '',
      minWidth: width,
      maxWidth: width,
      width,
      pinned: 'left',
      sortable: false,
      suppressHeaderMenuButton: true,
      suppressSizeToFit: false,
      lockPinned: true,
      lockPosition: true,
      lockVisible: true,
      cellEditorParams: <IFormGridColDefParam>{
        isActionCell: true,
        onCancelEditing: this.onCancelEditing,
        isActionColumnDoubleWidth: this.isActionColumnDoubleWidth()
      },
      editable: this.allowEdit
    };
  }

  private onRowGroupOpened = (event:RowGroupOpenedEvent):void => {
    if (event.node.expanded) {
      event.node.setSelected(true);
      // close the other ones
      event.api.forEachNode((rowNode:RowNode):void => {
        if (rowNode.id !== event.node.id) {
          rowNode.setExpanded(false);
        }
      });
    }
  };
  
  public addRow():void {
    // mark that a new row is being added
    this.isAddMode = true;

    // add an empty row
    this.gaAgGridService.applyTransaction(this.gridId, { add: [ this.getColDefaultValues() ], addIndex: 0 });

    this.startRowEditing();
  }

  private getColDefaultValues():IObject {
    const defaultValues:IObject = {};
    _.each(this.gridOptions.columnDefs, (columnDef:IFormGridColDef) => {
      if(columnDef?.field && columnDef?.cellEditorParams?.defaultValue){
        defaultValues[columnDef.field] = columnDef.cellEditorParams.defaultValue;
      }
    });

    return defaultValues;
  }

  private deleteRow(data:any):void {
    this.gaAgGridService.applyTransaction(this.gridId, {
      remove: [ data ]
    });

    this.onChange.emit(this.getModel());
  }

  private startRowEditing(rowIndex?:number):void {
    // because editType: 'fullRow' -> full row will become start becoming editable
    this.gaAgGridService.startEditing(this.gridId, {
      rowIndex: rowIndex || 0,
      colKey: this.getColDefs()[0].field // use first column
    });
  }

  private onCellValueChanged = (event:CellValueChangedEvent):void => {
    // notify other components about the cell value change
    this.eventService.sendMessage(EVENT_ROW_DATA_CHANGED, event);
  };

  private onRowEditingStarted = () => {
    if(this.allowEdit) {
      this.statusActionButtons(true);
      this.preventExitEditing();

      this.aRowIsEditing = true;
      this.onRowEditing.emit(this.aRowIsEditing);
    }
  };

  private onRowEditingStopped = (event:RowEditingStoppedEvent) => {
    if(this.isAddMode && _.isEqual(event.data, this.getColDefaultValues())){
      // determine if anything has changed in the form, and when no changes -> delete the row
      this.deleteRow(event.data);
    } else {
      this.onChange.emit(this.getModel());
    }

    this.clear();
  };

  private onCancelEditing = (rowIndex:number):void => {
    if(this.isAddMode){
      // when a new row is cancelled -> delete the row
      const cancelledRow:IRowNode = this.gaAgGridService.getDisplayedRowAtIndex(this.gridId, rowIndex);
      this.deleteRow(cancelledRow.data);
    } else {
      // when editing an existing row and cancel -> revert to the previous values and cancel editing
      this.gaAgGridService.stopEditing(this.gridId, true);
    }

    this.clear();
  };

  private getModel():IObject[] {
    const rowData:IObject[] = [];
    this.gaAgGridService.forEachNode(this.gridId, (rowNode:RowNode) => rowData.push(rowNode.data));
    return rowData;
  }

  private isCellEditable(params:EditableCallbackParams, initialEditable:boolean|EditableCallback):boolean {
    // when a function is passed in the configuration -> skip the logic from this method
    if(_.isFunction(initialEditable)){
      return initialEditable(params);
    }

    // check if the editable is allowed on add or update
    if(this.isAddMode && params.colDef.cellEditorParams?.editableOnAdd || !this.isAddMode && params.colDef.cellEditorParams?.editableOnUpdate){
      return true;
    }

    return initialEditable;
  }

  // prevent getting out of row editing when clicking on a different row
  // remove pointer-events-none class is done by rowClassRules
  private preventExitEditing():void {
    if(this.gridOptions.context?.preventExitEditing) {
      const nonSelectedGridRows:JQuery<HTMLElement> = $(CSS_NON_SELECTED_ROWS);
      _.each(nonSelectedGridRows, (row:HTMLElement) => {
        row.classList.add('pointer-events-none');
      });
    }
  }

  private statusActionButtons(disabled:boolean):void {
    const nonSelectedGridRows:JQuery<HTMLElement> = $(CSS_NON_SELECTED_ROWS);
    nonSelectedGridRows.find('.btn-delete').prop('disabled', disabled);
    nonSelectedGridRows.find('.btn-edit').prop('disabled', disabled);
  }

  private isActionColumnDoubleWidth():boolean {
    return this.allowEdit && this.allowDelete;
  }

  // format the value that is copied to clipboard
  private processCellForClipboard = (params:ProcessCellForExportParams):any => {
    if (!params.column) {
      return;
    }

    const colDef:IColDef = params.column.getColDef();
    return (<any>colDef).valueFormatter ? (<any>colDef).valueFormatter(params) : params.value;
  };

  // this listens to every click, checks when it is clicked outside ag-grid container call stopEditing (manually handling stopEditingWhenCellsLoseFocus)
  @HostListener('window:click', ['$event'])
  public onClick(event:Event):void {
    // apply only when:
    //    stopEditingWhenCellsLoseFocus is configured to false, meaning that ag-grid doesn't call stopEditing when clicking outside ag-grid
    //    aRowIsEditing is true, meaning that currently a row is set for editing
    //    $(event.target).closest('form-grid').length !== 1 -> clicked element is outside ag-grid container
    //    $(event.target).closest('.pika-single').length -> clicked element is part of date picker popup, so that it ignores the closing of popup)
    if(util.notNullOrUndefined(this.gridOptions.stopEditingWhenCellsLoseFocus) && !this.gridOptions.stopEditingWhenCellsLoseFocus && this.aRowIsEditing && $(event.target).closest('form-grid').length !== 1 && $(event.target).closest('.pika-single').length !== 1){
      this.gaAgGridService.stopEditing(this.gridId);
    }
  }

  private clear():void {
    this.statusActionButtons(false);
    this.isAddMode = undefined;
    this.aRowIsEditing = false;
    this.onRowEditing.emit(this.aRowIsEditing);
  }
}